/**
 * The screens that take no input: the outcome, the waits, and the heading every
 * collect step opens with. Shared by `FlowRunner` and the console's flow preview
 * (`routes/PreviewFlowScreen`), so the preview shows these words, not a copy.
 */
import { useTranslation } from 'react-i18next';

import { Illustration } from '../ui/Illustration';
import { StatusGlyph } from '../ui/StatusGlyph';
import type { Screen } from './screen';
import { translateState } from './stateLabel';

type Outcome = Extract<Screen, { kind: 'terminal' }>['outcome'];

export function TerminalScreen({ outcome }: { outcome: Outcome }) {
  const { t } = useTranslation();
  const ok = outcome === 'approved' || outcome === 'completed';
  return (
    <main className="screen">
      <div className="card enter">
        <StatusGlyph kind={ok ? 'success' : 'error'} />
        <h1 className="step-title">{t(`flow.terminal.${outcome}`, { defaultValue: outcome })}</h1>
      </div>
    </main>
  );
}

/**
 * Distinct from `review`. Nothing is being reviewed here: the workflow declares
 * this control unimplemented and parks the case until an operator records an
 * attestation. Showing the generic "Under review" copy would tell the applicant
 * a 30-second check is running when the wait is open-ended.
 */
export function BlockedScreen() {
  const { t } = useTranslation();
  return (
    <main className="screen">
      <div className="card enter">
        <StatusGlyph kind="wait" />
        <h1 className="step-title">
          {t('flow.blocked.heading', { defaultValue: 'Waiting on a reviewer' })}
        </h1>
        <p className="step-text">
          {t('flow.blocked.body', {
            defaultValue:
              'This step needs a member of our team to check it manually. You can close this page — we will be in touch.',
          })}
        </p>
      </div>
    </main>
  );
}

/**
 * An automated check (`pending_process`: face match, laser code, reports) is
 * seconds long and can hand back to the applicant — in kyc_thai_v3/v4 the
 * signature step comes AFTER these. Telling them they may close the page here
 * would lose them before they sign, so this wait says the opposite.
 */
export function CheckingScreen() {
  const { t } = useTranslation();
  return (
    <main className="screen">
      <div className="card card--hero enter" data-testid="checking">
        <span className="spinner spinner--hero" aria-hidden="true" />
        <h1 className="hero-title">{t('flow.checking.heading', { defaultValue: 'Just a moment' })}</h1>
        <p className="hero-text">
          {t('flow.checking.body', {
            defaultValue: 'This takes a few seconds. Keep this page open.',
          })}
        </p>
      </div>
    </main>
  );
}

export function ReviewScreen() {
  const { t } = useTranslation();
  return (
    <main className="screen">
      <div className="card card--hero enter">
        <Illustration kind="submitted" />
        <h1 className="hero-title">{t('flow.review.heading', { defaultValue: 'Submitted' })}</h1>
        <p className="hero-text">
          {t('flow.review.body', {
            defaultValue: 'We’re checking your details. You can close this page.',
          })}
        </p>
      </div>
    </main>
  );
}

/**
 * Step copy: friendly title with a LEAF FALLBACK, plus an optional guidance line
 * rendered only when translated.
 *
 * The leaf fallback matters because state paths are dotted and compound nesting
 * is an authoring choice: `data_collection.awaiting_consent` in one template is
 * `awaiting_consent` in another, and a fork can move a step between compounds
 * without changing what the step IS. Keying only on the full path means every
 * such flow renders a raw dotted key at the applicant.
 */
export function StepHeading({ state }: { state: string | null }) {
  const { t } = useTranslation();
  const title = translateState(t, 'flow.step', state, state ?? '');
  const hint = translateState(t, 'flow.hint', state, '');
  return (
    <>
      <h1 className="step-title">{title}</h1>
      {hint ? <p className="step-text">{hint}</p> : null}
    </>
  );
}
